// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography, Button, IconButton, Grid2 } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { TFunction } from 'i18next';
// Components, Layouts, Pages
import { BaseDatePicker, BaseLabel, BasePieChart, BaseTable, Loading, UnderDevelopment } from '~/components/common';
// Others
import { IClientIncomeReport, IQueryParamListExport } from '~/utils/interface/reports';
import { IBasePieData, IHeaderCellType } from '~/utils/interface/common';
import { formatCurrencyUSD, formatNumberShort } from '~/utils/helper/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { exportDataReport, getListClientIncomeReport } from '~/thunks/reports/reportsThunk';
import { BACK_TO_PREVIOUS_PAGE } from '~/utils/constants/common';
// Styles, images, icons
import styles from './ClientIncomeReport.module.scss';
import { icons } from '~/assets';
import { ReportTypeEnum } from '~/utils/enum/common';

const cx = classNames.bind(styles);

const columns = (t: TFunction): IHeaderCellType<IClientIncomeReport>[] => {
  return [
    {
      id: 'name',
      width: '50%',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_name')}</Box>
        </Stack>
      ),
      render: (record: IClientIncomeReport) => record?.name,
    },
    {
      id: 'revenue',
      width: '50%',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_revenue')}</Box>
        </Stack>
      ),
      render: (record: IClientIncomeReport) => formatCurrencyUSD({ value: record?.revenue, showDecimal: true }),
    },
  ];
};

const ClientIncomeReport = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/reports', 'common/base']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [clientIncomeReportList, setClientIncomeReportList] = useState<IClientIncomeReport[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [paramsObject, setParamsObject] = useState<IQueryParamListExport>({});
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListSubRentalsReport();
  }, []);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListSubRentalsReport = () => {
    setIsLoading(true);

    dispatch(getListClientIncomeReport(paramsObject))
      .unwrap()
      .then((res) => {
        const { data } = res;

        if (!data) return;

        setClientIncomeReportList(data);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleApplyFilter = () => {
    handleGetListSubRentalsReport();
  };

  const handleExport = () => {
    setIsLoading(true);

    const params: IQueryParamListExport = {
      ...paramsObject,
      type: ReportTypeEnum.CLIENT_INCOME,
    };

    dispatch(exportDataReport(params))
      .unwrap()
      .then((res) => {
        const { data } = res;

        handleDownloadFile(data?.url);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleDownloadFile = (fileUrl: string) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileUrl;
    link.click();
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const getTotalRevenue = () => {
    return clientIncomeReportList.reduce((sum, e) => sum + Number(e.revenue), 0);
  };

  const convertPieChartData: IBasePieData[] = (() => {
    const colors = ['#2D80FC', '#FEA500', '#F86B38', '#77B900', '#EC4899'];

    const sorted = clientIncomeReportList
      .map((e) => ({ ...e, revenue: Number(e.revenue) }))
      .sort((a, b) => b.revenue - a.revenue);

    const top4 = sorted.slice(0, 4);
    const others = sorted.slice(4);
    const otherTotal = others.reduce((sum, e) => sum + e.revenue, 0);
    const totalRevenue = sorted.reduce((sum, e) => sum + e.revenue, 0);

    const top4Data = top4.map((e, i) => ({
      id: e.name,
      label: formatNumberShort(e.revenue),
      value: e.revenue,
      color: colors[i],
      percent: Math.round((e.revenue / totalRevenue) * 100),
    }));

    const filteredTop4 = top4Data.filter((e) => e.value > 0);

    const otherPercent = 100 - filteredTop4.reduce((sum, e) => sum + e.percent, 0);

    const result = [
      ...filteredTop4,
      ...(otherTotal > 0
        ? [
            {
              id: t('reports_other_events_label'),
              label: formatNumberShort(otherTotal),
              value: otherTotal,
              color: colors[4],
              percent: otherPercent,
            },
          ]
        : []),
    ];

    return result;
  })();

  const handleBack = () => {
    navigate(BACK_TO_PREVIOUS_PAGE);
  };
  //#endregion Handle Function

  return (
    <Stack id='quarantineReportPage' className={cx('container')} spacing={6}>
      <Stack direction='row' spacing={2} alignItems='center'>
        <IconButton color='primary' onClick={handleBack} className={cx('buttonBack')}>
          <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} />
        </IconButton>

        <Stack direction={'column'} spacing={2}>
          <BaseLabel label={t('reports_client_income_title')} styleType='bigTitle' />
          <Typography className={cx('description')}>{t('reports_client_income_desc_page')}</Typography>
        </Stack>
      </Stack>

      <Stack direction={'column'} gap={5} className={cx('viewAction')}>
        <Grid2 columnSpacing={'24px'} alignItems={'start'} rowSpacing={'24px'} container>
          <Grid2 size={3}>
            <BaseDatePicker
              mode='date'
              height={40}
              label={t('common_text_start_date', { ns: 'common/base' })}
              value={paramsObject.startDate}
              onChange={(date) => {
                setParamsObject((prevState) => ({
                  ...prevState,
                  startDate: date as string,
                }));
              }}
            />
          </Grid2>

          <Grid2 size={3}>
            <BaseDatePicker
              mode='date'
              height={40}
              label={t('common_text_end_date', { ns: 'common/base' })}
              value={paramsObject.endDate}
              onChange={(date) => {
                setParamsObject((prevState) => ({
                  ...prevState,
                  endDate: date as string,
                }));
              }}
            />
          </Grid2>
        </Grid2>

        <Stack direction='row' justifyContent={'space-between'} alignItems='center'>
          <Button
            type='button'
            variant='contained'
            className={cx('buttonApply')}
            onClick={handleApplyFilter}
            startIcon={<img src={icons.commonIconFilter} alt={t('common_alt_img', { ns: 'common/base' })} />}
          >
            {t('common_action_apply', { ns: 'common/base' })}
          </Button>

          <Button
            type='button'
            variant='contained'
            className={cx('buttonExport')}
            onClick={handleExport}
            disabled={!clientIncomeReportList.length}
            startIcon={<img src={icons.commonIconExportExcel} alt={t('common_alt_img', { ns: 'common/base' })} />}
          >
            {t('common_button_export_excel', { ns: 'common/base' })}
          </Button>
        </Stack>
      </Stack>

      <Stack direction={'row'} className={cx('viewContent')} spacing={'100px'}>
        <Stack direction={'column'} spacing={2}>
          <Stack direction={'column'} spacing={2}>
            <Typography className={cx('subTitle')}>{t('reports_client_income_revenue_by_event_type')}</Typography>
            <Typography className={cx('subDescription')}>
              {t('reports_client_income_total_revenue', { total: formatCurrencyUSD({ value: getTotalRevenue() }) })}
            </Typography>
          </Stack>

          <BasePieChart data={convertPieChartData} />
        </Stack>

        <Stack direction='column' spacing={2} width={'100%'}>
          {convertPieChartData.map((item, index) => (
            <Stack key={item.id} className={cx('itemChart')}>
              <Stack direction='row' alignItems='center' spacing={3}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    backgroundColor: item.color,
                  }}
                />
                <Stack>
                  <Typography className={cx('eventName')}>{item.id}</Typography>
                  <Typography className={cx('eventPercentage')}>
                    {t('reports_percentage_of_total', { percentage: item.percent })}
                  </Typography>
                </Stack>
              </Stack>

              <Stack>
                <Typography className={cx('eventValue')}>
                  {formatCurrencyUSD({ value: item.value, showDecimal: false })}
                </Typography>
                <Typography className={cx('eventPercentage')}>{t('report_table_revenue')}</Typography>
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Stack>

      <BaseTable dataSource={clientIncomeReportList} columns={columns(t)} />

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}

      {isLoading && <Loading />}
    </Stack>
  );
};

export default ClientIncomeReport;
